import { shopModeApi } from "../../api/shopMode";
import { isPrinterSupported, printRaster } from "../../platform/printer";
import {
  resolvePrinterFor,
  type ResolvedPrinter,
} from "../../platform/printerRegistry";
import { tuningToJobOptions } from "../../platform/printSettings";
import { toCatPrinterJob } from "../../receipt/catPrinter";
import { renderReceipt, toMonoBitmap } from "../../receipt/receiptRender";
import {
  RECEIPT_WIDTH_DOTS,
  templateFromStored,
  type ReceiptTemplate,
} from "../../receipt/receiptTemplate";

/**
 * G-156 — printing a sale's courtesy ticket, in one place (OD-34, KI-68).
 *
 * The same four steps as `printItemSticker.ts`, for the ticket: fetch the
 * sale's receipt data, the shop's design and its logo; draw the ticket
 * through the SAME `renderReceipt` the office preview uses, so what the
 * owner designed is what comes out; pack it for this printer's heat; send it.
 *
 * THE PRINTER IS AN ARGUMENT, resolved once by the caller. The ticket is
 * drawn at the roll's 384 dots (`RECEIPT_WIDTH_DOTS`, what the design was
 * laid out for) and the row width goes with it, never re-read from a second
 * registry lookup — see `printerRegistry.ts` on why there is no `printTo`.
 *
 * NO WORDS LIVE HERE. Every failure comes back as an outcome with its cause;
 * the screen chooses the sentence through the dictionaries.
 */

export type SaleReceiptPrint =
  | { outcome: "printed" }
  /** The data, design or logo did not come: the server, or the network. */
  | { outcome: "could-not-load"; cause: unknown }
  | { outcome: "could-not-draw"; cause: unknown }
  | { outcome: "could-not-print"; cause: unknown };

/**
 * Whether this page can offer the receipt step at all. The tablet app, where
 * the Bluetooth bridge is; and the development server, so the step can be
 * seen in a browser (Chrome's own Web Bluetooth then does the pairing).
 * A plain browser on the live site never sees the question (OD-34).
 */
export function receiptStepAvailable(isDevBuild: boolean): boolean {
  return isPrinterSupported() || isDevBuild;
}

/**
 * The printer a receipt goes to, or null when nothing is paired. Read per
 * sale, not once at mount: a florist may pair from the question card itself.
 * Since OD-250 a tablet with only a sticker printer prints the receipt there.
 */
export function saleReceiptPrinter(): ResolvedPrinter | null {
  return resolvePrinterFor("receipts");
}

/** An `<img>` the canvas can draw, or null when the shop has no logo. */
function loadLogo(url: string | null): Promise<HTMLImageElement | null> {
  if (!url) return Promise.resolve(null);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("logo"));
    img.src = url;
  });
}

async function loadDesign(): Promise<{ template: ReceiptTemplate; logoUrl: string | null }> {
  const template = templateFromStored(await shopModeApi.floorReceiptTemplate());
  const logoUrl = template.logoKey ? await shopModeApi.floorReceiptLogoBlobUrl() : null;
  return { template, logoUrl };
}

/**
 * Fetch, draw, print. NEVER THROWS: each way this can fail is something the
 * florist can act on (printer asleep, Bluetooth off, the network), so each
 * comes back as an outcome rather than an exception while paper is involved.
 */
export async function printSaleReceipt(
  printer: ResolvedPrinter,
  saleId: string,
): Promise<SaleReceiptPrint> {
  let bytes: Uint8Array;
  let logoUrl: string | null = null;
  try {
    const [data, design] = await Promise.all([
      shopModeApi.floorReceipt(saleId),
      loadDesign(),
    ]);
    logoUrl = design.logoUrl;
    let logo: HTMLImageElement | null;
    try {
      logo = await loadLogo(logoUrl);
    } catch (cause: unknown) {
      return { outcome: "could-not-load", cause };
    }
    try {
      const canvas = renderReceipt(design.template, data, logo);
      bytes = toCatPrinterJob(toMonoBitmap(canvas), {
        ...tuningToJobOptions(printer.printer.tuning),
        rowBytes: Math.ceil(RECEIPT_WIDTH_DOTS / 8),
      });
    } catch (cause: unknown) {
      return { outcome: "could-not-draw", cause };
    }
  } catch (cause: unknown) {
    return { outcome: "could-not-load", cause };
  } finally {
    if (logoUrl) URL.revokeObjectURL(logoUrl);
  }
  try {
    await printRaster(printer.printer, bytes);
  } catch (cause: unknown) {
    return { outcome: "could-not-print", cause };
  }
  return { outcome: "printed" };
}
